import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  toast,
  Typography,
} from "@suraasa/placebo-ui"
import api from "api"
import { AuthData } from "api/resources/auth/types"
import { OTPResponse } from "api/resources/settings/types"
import clsx from "clsx"
import ResendOTPButton from "components/ResendOTPButton"
import { useGlobalContext } from "global/Context/context"
import { useEffect, useState } from "react"
import { useGoogleReCaptcha } from "react-google-recaptcha-hook"
import { Controller, useForm } from "react-hook-form"
import OTPInput from "react-otp-input"
import { mapErrorsV2 } from "utils/helpers"
import { CaptchaType } from "utils/hooks/useGRecaptcha"

const SignInEmailDialog = ({
  otpData,
  close,
  open,
  onVerify,
  email,
}: {
  otpData: OTPResponse
  close: () => void
  onVerify: () => void
  onBack: () => void
  email: string
  open: boolean
}) => {
  const [resendAt, setResendAt] = useState("")

  const { executeGoogleReCaptcha } = useGoogleReCaptcha(
    import.meta.env.VITE_INVISIBLE_RECAPTCHA_KEY,
    {
      enterprise: true,
    }
  )

  const {
    control,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<{ otp: string }>({
    defaultValues: {
      otp: "",
    },
  })

  const { setAuthInfo } = useGlobalContext()

  useEffect(() => {
    if (!open) reset()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const onSubmit = handleSubmit(async ({ otp }) => {
    try {
      const res = await api.settings.verifyOtpForSignIn({
        data: {
          token: otpData.token,
          otp,
          platform: "Student",
        },
      })

      if (!res.platform) {
        throw new Error("Platform data not found")
      }

      const authInfo: AuthData = {
        accessToken: res.platform.accessToken,
        expiresIn: res.platform.expiresIn,
        refreshToken: res.platform.refreshToken,
        user: {
          uuid: res.user.uuid,
          email: res.user.email,
          firstName: res.user.firstName,
          lastName: res.user.lastName || "",
          middleName: res.user.middleName,
          username: res.user.username || "",
        },
        generatedAt: Date.now(),
        expiresAt: Date.now() + res.platform.expiresIn * 1000,
      }
      onVerify()
      setAuthInfo(authInfo)
      reset()
    } catch (err) {
      mapErrorsV2(err, {
        fields: [["otp"]],
        setter: setError,
      })
    }
  })

  const resendOtp = async () => {
    const captcha = {
      value: await executeGoogleReCaptcha("auth"),
      type: CaptchaType.invisible,
    }

    if (!captcha) {
      toast.error("Please verify the captcha")
      return
    }

    try {
      const res = await api.settings.sendOTP({
        data: {
          token: otpData.token,
          captcha,
        },
      })
      setResendAt(res.resendAt)
      toast.info("We've sent you a new code")
    } catch (e) {
      mapErrorsV2(e)
    }
  }

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-[400px]">
        <form onSubmit={onSubmit}>
          <DialogHeader>
            <DialogTitle>Verify your account</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <div className="p-1">
              <Typography
                variant="smallBody"
                className="mb-0.25 text-onSurface-500"
              >
                A 6 digit code has been sent to
              </Typography>
              <Typography
                variant="strongSmallBody"
                className="mb-3 block text-onSurface-900"
              >
                {email}
              </Typography>

              <Controller
                control={control}
                name="otp"
                render={({ field }) => (
                  <OTPInput
                    numInputs={6}
                    {...field}
                    containerStyle="justify-between"
                    inputStyle={clsx(
                      "h-[50px] !w-5 !rounded-[4px] border-2 border-onSurface-500 text-title2 !ring-interactive-400 focus-within:!ring-2 focus-within:!ring-offset-1 focus:outline-onSurface-800",
                      {
                        "!border-critical-500": Boolean(errors.otp),
                      }
                    )}
                    inputType="number"
                    renderInput={props => <input {...props} />}
                    shouldAutoFocus
                    onChange={otp => {
                      field.onChange(otp)
                      if (otp.length === 6) {
                        onSubmit()
                      }
                    }}
                  />
                )}
                rules={{
                  required: { value: true, message: "Required" },
                }}
              />
              {Boolean(errors.otp) && (
                <Typography
                  className="mt-1 text-critical-500"
                  variant="smallBody"
                >
                  {errors.otp?.message}
                </Typography>
              )}

              <Typography
                variant="smallBody"
                className="mt-3 text-onSurface-500"
              >
                This email address will be used to access your PgCTL account.
              </Typography>

              <div className="mt-3 flex flex-wrap items-center">
                <Typography
                  variant="smallBody"
                  className="me-1 shrink-0 text-onSurface-500"
                >
                  {/* eslint-disable-next-line @cspell/spellchecker */}
                  Didn&apos;t receive the code?
                </Typography>
                <ResendOTPButton
                  resendAt={resendAt || otpData.resendAt}
                  text="Send Again"
                  onClick={resendOtp}
                />
              </div>
            </div>
          </DialogBody>

          <DialogFooter>
            <Button
              size="sm"
              type="submit"
              color="black"
              loading={isSubmitting}
            >
              Verify &amp; Continue
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default SignInEmailDialog
